// ============================================================
// TERROR — THE CODEX
// The Grand Archive — everything you've discovered, earned,
// unlocked, and achieved. One place for your entire journey.
//
// Tabs:
//   ◆ Collection  — cards owned/unowned, holo, filters
//   ◈ Story       — story variants + easter egg variants
//   ◇ Pages       — grimoire page fragments
//   ✦ Companions  — plaza companions unlocked
//   ⊡ Records     — wins, dungeons, ELO, stat shrine
// ============================================================

import { useState, useMemo } from "react";
import {
  GM, OBSIDIAN_BG, OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS,
  OBSIDIAN_PANEL, GOLD_METAL_H, facetTB,
  SHADOW_RAISED, SHADOW_RECESS, SHADOW_FLOAT, textGlow, TEXT_SET,
} from "./theme";
import { StarDust, NoiseOverlay, UnstableWorldBG } from "./TerrorUI";
import { useStageRect } from "./RoomBackdrop"; // C5 — the one canonical room rectangle
import { cardUrl } from "./CDN"; // CDN law — one domain, one file (loose-debt sweep 2026-08-18)
import { useModalKeys } from "./KeyboardNav";
import HoloFoil from "./HoloFoil";
import SourceSigil, { primaryOrigin } from "./SourceSigil";
import TheMark from "./TheMark";
import { getCardOrigins } from "./progression";
import {
  ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES,
  PROPHECIES, CURIOS, ESCLINGS,
  // CARD_TYPES so the card page can ask "is this an Affinity" by the
  // catalogue's own constant rather than by a retyped string.
  CARD_TYPES,
} from "./cards";
import { loreFor, isAuthored } from "./cardLore"; // H14

// GM now imports from theme.js v4 (Gilded Obsidian) — the local
// vellum-brown copy and MANUSCRIPT_BG are DEAD. One source of truth.



// ── ALL COLLECTIBLE CARDS ─────────────────────────────────────
const ALL_COLLECTIBLE = [
  ...ESCENDANTS, ...TOMES, ...IMPERA,
  ...UHLTIS, ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS,
];
const TOTAL = ALL_COLLECTIBLE.length;

// ── HOLO TIERS ───────────────────────────────────────────────
const HOLO_TIERS = {
  holo_mythic: { label: "◆ Mythic",  color: "#ff66cc", borderColor: "#ff66cc" },
  holo_gold:   { label: "✦ Gold",    color: GM.inkGold,   borderColor: "#d4a84b" },
  holo_silver: { label: "◈ Silver",  color: "#ccc",    borderColor: "#aaaaaa" },
  holo_void:     { label: "◈ Void",     color: "#9333ea", borderColor: "#7c3aed" },
  holo_solrend:  { label: "◆ Solrend",  color: "#ff2266", borderColor: "#cc0033" },
  holo_aeternus: { label: "✦ A'eterni", color: "#f0f0ff", borderColor: "#ccccff" },
  holo_shadow:   { label: "◇ Shadow",   color: "#9966ff", borderColor: "#6600cc" },
  holo_depths:   { label: "◆ Depths",   color: "#00aaff", borderColor: "#0077cc" },
};

// ── CODEX KEYFRAMES — imperative injection to document.head, keyed
// off node existence. The <style>-block keyframe trap is closed in
// this file (theme.js Material Law 6). CSS preserved verbatim.
function ensureCodexKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("cx-keyframes")) return;
  const el = document.createElement("style");
  el.id = "cx-keyframes";
  el.textContent = `
      /* ── HOLO SHIFT BASE ── */
      @keyframes cx-holoShift {
        0%   { background-position:0% 50%;   filter:hue-rotate(0deg)   brightness(1.15); }
        50%  { background-position:100% 50%; filter:hue-rotate(180deg) brightness(1.35); }
        100% { background-position:0% 50%;   filter:hue-rotate(360deg) brightness(1.15); }
      }
      @keyframes cx-silverSweep {
        0%   { background-position:200% 0%; opacity:0.55; }
        40%  { background-position:0% 100%; opacity:0.85; }
        60%  { background-position:-50% 150%; opacity:0.55; }
        100% { background-position:200% 0%; opacity:0.55; }
      }
      @keyframes cx-goldSweep {
        0%   { background-position:200% 0%; opacity:0.55; }
        40%  { background-position:0% 100%; opacity:0.90; }
        60%  { background-position:-50% 150%; opacity:0.55; }
        100% { background-position:200% 0%; opacity:0.55; }
      }
      @keyframes cx-glowSilver {
        0%,100% { box-shadow:0 0 6px #aaaaaa55, 0 0 12px #cccccc33, 0 0 0 1px #aaaaaa88; }
        50%      { box-shadow:0 0 14px #ddddddaa, 0 0 28px #cccccc66, 0 0 0 1px #eeeeeecc; }
      }
      @keyframes cx-glowGold {
        0%,100% { box-shadow:0 0 10px #d4a84baa, 0 0 22px #d4a84b55, 0 0 0 1px #d4a84bcc; }
        50%      { box-shadow:0 0 20px #ffd700cc, 0 0 40px #d4a84b88, 0 0 0 2px #ffd700; }
      }
      @keyframes cx-glowMythic {
        0%   { box-shadow:0 0 16px #ff006688, 0 0 32px #7c3aed55, 0 0 0 2px #ff66cc; }
        33%  { box-shadow:0 0 16px #00ff8866, 0 0 32px #3b82f655, 0 0 0 2px #00ffcc; }
        66%  { box-shadow:0 0 16px #ffcc0066, 0 0 32px #ff660055, 0 0 0 2px #ffaa00; }
        100% { box-shadow:0 0 16px #ff006688, 0 0 32px #7c3aed55, 0 0 0 2px #ff66cc; }
      }
      @keyframes cx-voidPulse    { 0%,100%{filter:hue-rotate(240deg) brightness(0.9) saturate(2);} 50%{filter:hue-rotate(275deg) brightness(1.2) saturate(3);} }
      @keyframes cx-solrendPulse { 0%,100%{filter:hue-rotate(330deg) brightness(1.1) saturate(2.5);} 50%{filter:hue-rotate(345deg) brightness(1.5) saturate(3.5);} }
      @keyframes cx-aeternus     { 0%,100%{filter:brightness(1.2) contrast(0.9) saturate(0.1);} 50%{filter:brightness(2.0) contrast(0.8) saturate(0);} }
      @keyframes cx-shadowP      { 0%,100%{filter:invert(0.85) hue-rotate(190deg) brightness(0.65);} 50%{filter:invert(0.95) hue-rotate(225deg) brightness(0.85);} }
      @keyframes cx-glowVoid     { 0%,100%{box-shadow:0 0 14px #6600ccaa,0 0 28px #3b076466,0 0 0 2px #9333ea;} 50%{box-shadow:0 0 28px #9933ffcc,0 0 56px #6600cc88,0 0 0 2px #cc66ff;} }
      @keyframes cx-glowSolrend  { 0%,100%{box-shadow:0 0 14px #ff2266bb,0 0 28px #cc003355,0 0 0 2px #ff2266;} 50%{box-shadow:0 0 28px #ff4488dd,0 0 56px #ff226688,0 0 0 2px #ff88aa;} }
      @keyframes cx-glowAeternus { 0%,100%{box-shadow:0 0 12px #ffffff77,0 0 24px #ccccff44,0 0 0 2px #ffffffbb;} 50%{box-shadow:0 0 28px #ffffffcc,0 0 56px #ccccff88,0 0 0 2px #ffffff;} }
      @keyframes cx-glowShadow   { 0%,100%{box-shadow:0 0 14px #6600ccaa,0 0 28px #2d006655,0 0 0 2px #9966ff;} 50%{box-shadow:0 0 28px #9933cccc,0 0 56px #440099aa,0 0 0 2px #cc99ff;} }
      .cx-holo-silver  { animation:cx-glowSilver  3s ease-in-out infinite; }
      .cx-holo-gold    { animation:cx-glowGold    2.5s ease-in-out infinite; }
      .cx-holo-mythic  { animation:cx-glowMythic  2s linear infinite; }
      .cx-holo-void    { animation:cx-glowVoid    2.5s ease-in-out infinite; }
      .cx-holo-solrend { animation:cx-glowSolrend 2s ease-in-out infinite; }
      .cx-holo-aeternus{ animation:cx-glowAeternus 3s ease-in-out infinite; }
      .cx-holo-shadow  { animation:cx-glowShadow  2.5s ease-in-out infinite; }
        .cx-holo-depths  { animation:cx-depthsGlow  2.5s ease-in-out infinite; }
      .cx-foil-silver  { background:linear-gradient(115deg,transparent 20%,#ffffff55 40%,#ccccdd88 50%,#ffffff55 60%,transparent 80%); background-size:300% 300%; animation:cx-silverSweep 3s ease-in-out infinite; mix-blend-mode:overlay; pointer-events:none; position:absolute; inset:0; border-radius:inherit; z-index:1; }
      .cx-foil-gold    { background:linear-gradient(115deg,transparent 20%,#ffd70066 38%,#ffcc0099 50%,#ffd70066 62%,transparent 80%); background-size:300% 300%; animation:cx-goldSweep 2.5s ease-in-out infinite; mix-blend-mode:overlay; pointer-events:none; position:absolute; inset:0; border-radius:inherit; z-index:1; }
      .cx-foil-mythic  { background:linear-gradient(125deg,#ff0066,#ff6600,#ffcc00,#00ff88,#00ccff,#6600ff,#ff0066); background-size:400% 400%; animation:cx-holoShift 3s linear infinite; opacity:0.35; mix-blend-mode:color-dodge; pointer-events:none; position:absolute; inset:0; border-radius:inherit; z-index:1; }
      .cx-foil-void    { background:linear-gradient(125deg,#3b0764,#1a0035,#6600cc,#220044,#3b0764); background-size:400% 400%; animation:cx-voidPulse 4s ease-in-out infinite; opacity:0.50; mix-blend-mode:color-dodge; pointer-events:none; position:absolute; inset:0; border-radius:inherit; z-index:1; }
      .cx-foil-solrend { background:linear-gradient(125deg,#cc0000,#ff2266,#ff0033,#cc0022,#ff0055); background-size:400% 400%; animation:cx-solrendPulse 3s ease-in-out infinite; opacity:0.48; mix-blend-mode:color-dodge; pointer-events:none; position:absolute; inset:0; border-radius:inherit; z-index:1; }
      .cx-foil-aeternus{ background:linear-gradient(125deg,#ffffff,#e8e8ff,#ffffff,#d8d8ee,#ffffff); background-size:300% 300%; animation:cx-aeternus 4s ease-in-out infinite; opacity:0.40; mix-blend-mode:screen; pointer-events:none; position:absolute; inset:0; border-radius:inherit; z-index:1; }
      .cx-foil-depths  { background:linear-gradient(125deg,#001a33,#003366,#00aaff,#004488,#001a33); background-size:400% 400%; animation:cx-depthsPulse 4s ease-in-out infinite; opacity:0.45; mix-blend-mode:color-dodge; pointer-events:none; position:absolute; inset:0; border-radius:inherit; z-index:1; }
        .cx-foil-shadow  { background:linear-gradient(125deg,#0a0020,#1a0035,#2d0066,#000005,#0a0020); background-size:400% 400%; animation:cx-shadowP 4s ease-in-out infinite; opacity:0.55; mix-blend-mode:multiply; pointer-events:none; position:absolute; inset:0; border-radius:inherit; z-index:1; }
      /* New limited edition effects — mapped to existing animations */
      @keyframes cx-voidPulse    { 0%,100%{filter:hue-rotate(240deg) brightness(0.8);} 50%{filter:hue-rotate(280deg) brightness(1.1);} }
      @keyframes cx-solrendPulse { 0%,100%{filter:hue-rotate(320deg) brightness(1.1) saturate(2);} 50%{filter:hue-rotate(340deg) brightness(1.4) saturate(2.5);} }

      /* ── DEPTHS SIGIL — cascading foil treatment (matches CV) ── */
      @keyframes cx-depthsSigilBreathe {
        0%,100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1);    filter: drop-shadow(0 0 8px #00aaff55); }
        50%     { opacity: 0.85; transform: translate(-50%, -50%) scale(1.04); filter: drop-shadow(0 0 14px #00aaffaa); }
      }
      @keyframes cx-depthsSigilShimmer { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 400; } }
      .cx-depths-sigil { position: absolute; left: 50%; top: 50%; width: 58%; aspect-ratio: 1 / 1; z-index: 3; pointer-events: none; animation: cx-depthsSigilBreathe 4s ease-in-out infinite; mix-blend-mode: screen; }
      .cx-depths-sigil svg { width: 100%; height: 100%; display: block; overflow: visible; }
      .cx-depths-sigil .sigil-outer  { stroke: #00aaff; stroke-width: 1.4; fill: none; }
      .cx-depths-sigil .sigil-mid    { stroke: #66ccff; stroke-width: 1.0; fill: none; }
      .cx-depths-sigil .sigil-core   { stroke: #99ddff; stroke-width: 0.8; fill: none; }
      .cx-depths-sigil .sigil-heart  { stroke: #ffffff; stroke-width: 0.5; fill: none; }
      .cx-depths-sigil .sigil-pinpoint { fill: #ffffff; }
      .cx-depths-sigil .sigil-shimmer { stroke: #ffffff; stroke-width: 0.7; fill: none; stroke-dasharray: 20 380; animation: cx-depthsSigilShimmer 3s linear infinite; opacity: 0.8; }
      .cx-depths-sigil .sigil-accent-line { stroke: #66ccff; stroke-width: 0.3; fill: none; opacity: 0.5; }

      @keyframes cx-depthsOrbit { 0% { transform: translate(-50%, -50%) rotate(0deg); } 100% { transform: translate(-50%, -50%) rotate(360deg); } }
      .cx-depths-orbit { position: absolute; left: 50%; top: 50%; width: 85%; aspect-ratio: 1 / 1; z-index: 2; pointer-events: none; animation: cx-depthsOrbit 20s linear infinite; mix-blend-mode: screen; opacity: 0.55; }
      .cx-depths-orbit svg { width: 100%; height: 100%; display: block; overflow: visible; }
      .cx-depths-orbit polygon { stroke: #00aaff; stroke-width: 0.8; fill: none; }

      @keyframes cx-depthsCornerBreathe { 0%,100% { opacity: 0.35; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.08); } }
      .cx-depths-corner { position: absolute; width: 14%; aspect-ratio: 1 / 1; z-index: 2; pointer-events: none; animation: cx-depthsCornerBreathe 3.2s ease-in-out infinite; mix-blend-mode: screen; filter: drop-shadow(0 0 3px #00aaff88); }
      .cx-depths-corner svg { width: 100%; height: 100%; display: block; }
      .cx-depths-corner polygon { stroke: #66ccff; stroke-width: 1.2; fill: none; }
      .cx-depths-corner-tl { top: 3%; left: 3%; }
      .cx-depths-corner-tr { top: 3%; right: 3%; animation-delay: 0.8s; }
      .cx-depths-corner-bl { bottom: 3%; left: 3%; animation-delay: 1.6s; }
      .cx-depths-corner-br { bottom: 3%; right: 3%; animation-delay: 2.4s; }

      @keyframes cx-depthsFleck { 0%,100% { opacity: 0; transform: scale(0.4); } 40% { opacity: 0.9; transform: scale(1); } 70% { opacity: 0.6; transform: scale(0.8); } }
      .cx-depths-fleck { position: absolute; width: 3%; aspect-ratio: 1 / 1; z-index: 2; pointer-events: none; mix-blend-mode: screen; animation: cx-depthsFleck 2.4s ease-in-out infinite; filter: drop-shadow(0 0 2px #66ccff); }
      .cx-depths-fleck svg { width: 100%; height: 100%; display: block; }
      .cx-depths-fleck polygon { fill: #ccefff; stroke: none; }
  `;
  document.head.appendChild(el);
}
ensureCodexKeyframes();

const TYPE_ICONS = {
  Escendant: "⬆", Tome: "📖", Impera: "✦", "U'hlti": "◈",
  Affinity: "◆", Prophecy: "◇", Curio: "💎", Escling: "🌱", All: "",
};
const TYPE_OPTIONS = ["All","Escendant","Tome","Impera","U'hlti","Affinity","Prophecy","Curio","Escling"];
// ── Rulez v2 naming (July 2026): display labels only. "U'hlti" is
// still the CODE id (vyren pattern) — players see U'hltima.
// The Primera→Impera row that used to live here is GONE: it existed
// only because cards.js typed the Luxmoire cards "Primera" while the
// canon called them Impera. cards.js says "Impera" now, so the type id
// and the label are the same string and the bridge is dead weight.
const TYPE_LABELS = { "U'hlti": "U'hltima" };

// ── TABS ─────────────────────────────────────────────────────
const TABS = [
  { id: "collection", label: "◆ Collection" },
  { id: "story",      label: "◈ Story"      },
  { id: "pages",      label: "◇ Pages"      },
  { id: "companions", label: "✦ Companions" },
  { id: "records",    label: "⊡ Records"    },
];

// ─────────────────────────────────────────────────────────────
// CARD TILE
// ─────────────────────────────────────────────────────────────
// CDN law — the domain lives in CDN.js alone; cardUrl also seal-strips.
const cardImageUrl = (id) => id ? cardUrl(id) : null;

function CardTile({ card, owned, holoData, onClick }) {
  const [imgErr, setImgErr] = useState(false);
  const holoTier = holoData?.holoTier;
  const holoColor = holoTier ? HOLO_TIERS[holoTier]?.color : null;
  const src = cardImageUrl(card.id);
  const glowClass = holoTier === "holo_mythic" ? "cx-holo-mythic"
    : holoTier === "holo_gold" ? "cx-holo-gold"
    : holoTier === "holo_silver" ? "cx-holo-silver"
    : holoTier ? "cx-holo-mythic" : "";
  return (
    <div onClick={() => onClick(card, holoData)} style={{
      width: 72, cursor: "pointer",
      opacity: owned ? 1 : 0.35,
      filter: owned ? "none" : "grayscale(60%)",
      transition: "all 0.15s",
      position: "relative",
    }}>
      <div className={glowClass} style={{
        borderRadius: 6, overflow: "hidden",
        border: holoColor ? `2px solid ${holoColor}` : `1px solid ${GM.inkBrown}55`,
        background: GM.pageWarm,
        aspectRatio: "2/3",
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative",
      }}>
        {src && !imgErr ? (
          <img src={src} alt={card.name} onError={() => setImgErr(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", position: "relative", zIndex: 0 }} />
        ) : (
          <div style={{ padding: 4, textAlign: "center" }}>
            <div style={{ fontSize: 7, color: GM.vellumFaint, letterSpacing: 0.5, fontFamily: GM.font }}>{card.name}</div>
          </div>
        )}
        {holoTier && <HoloFoil tier={holoTier} radius={6} corners={false} style={{ zIndex: 1 }} />}
      </div>
      {holoData?.holoCount > 0 && (
        <div style={{
          position: "absolute", top: 3, right: 3,
          background: holoColor, borderRadius: 3,
          fontSize: 6, color: "#000", fontWeight: 900,
          padding: "1px 3px", fontFamily: GM.font,
        }}>x{holoData.holoCount}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CARD DETAIL MODAL
// ─────────────────────────────────────────────────────────────
function CardDetailModal({ card, holoData, onClose }) {
  const [imgErr, setImgErr] = useState(false);
  // THE MARK (H18, doctrine §13) — the card's full Empire record, folded
  // behind a toggle so the modal's default read stays a card, not a form.
  const [showMark, setShowMark] = useState(false);
  const src = cardImageUrl(card.id);
  const holoTier = holoData?.holoTier;
  const holoColor = holoTier ? HOLO_TIERS[holoTier]?.color : null;
  const holoBorder = holoTier ? HOLO_TIERS[holoTier]?.borderColor : null;
  const glowClass = holoTier === "holo_mythic" ? "cx-holo-mythic"
                  : holoTier === "holo_gold" ? "cx-holo-gold"
                  : holoTier === "holo_silver" ? "cx-holo-silver"
                  : holoTier === "holo_void" ? "cx-holo-mythic"
                  : holoTier === "holo_solrend" ? "cx-holo-mythic"
                  : holoTier === "holo_aeternus" ? "cx-holo-gold"
                  : holoTier === "holo_shadow" ? "cx-holo-mythic" : holoTier === "holo_depths" ? "cx-holo-depths" : "";
  useModalKeys(onClose);
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(4,2,10,0.96)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 500, backdropFilter: "blur(8px)",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "min(92vw, 340px)",
        maxHeight: "92vh", overflowY: "auto",
        background: OBSIDIAN_PANEL,
        border: `1px solid ${holoBorder || GM.pageBorder}`,
        borderRadius: 14, padding: 20,
        boxShadow: SHADOW_FLOAT,
      }}>
        {/* ── ORIGIN — the one-origin law, worn BY the card: no text
            strip, just the sigil crowning the art (top-center below). */}
        {/* Card art with full holo foil */}
        <div className={glowClass} style={{ position: "relative", width: "100%", aspectRatio: "2/3", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
          {src && !imgErr ? (
            <img src={src} alt={card.name} onError={() => setImgErr(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", position: "relative", zIndex: 0 }} />
          ) : (
            <div style={{ width: "100%", height: "100%", background: GM.page, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${GM.inkBrown}55` }}>
              <span style={{ color: GM.vellumFaint, fontSize: 12 }}>{card.name}</span>
            </div>
          )}
          {holoTier && <HoloFoil tier={holoTier} radius={10} corners={holoTier !== "holo_depths"} style={{ zIndex: 1 }} />}
          {/* Depths cascading foil overlay — matches CV treatment */}
          {holoTier === "holo_depths" && (
            <>
              <div className="cx-depths-corner cx-depths-corner-tl"><svg viewBox="0 0 20 20"><polygon points="10,2 18,10 10,18 2,10" /></svg></div>
              <div className="cx-depths-corner cx-depths-corner-tr"><svg viewBox="0 0 20 20"><polygon points="10,2 18,10 10,18 2,10" /></svg></div>
              <div className="cx-depths-corner cx-depths-corner-bl"><svg viewBox="0 0 20 20"><polygon points="10,2 18,10 10,18 2,10" /></svg></div>
              <div className="cx-depths-corner cx-depths-corner-br"><svg viewBox="0 0 20 20"><polygon points="10,2 18,10 10,18 2,10" /></svg></div>
              {[
                { top: "18%", left: "30%", delay: "0s"   },
                { top: "28%", left: "68%", delay: "0.4s" },
                { top: "48%", left: "22%", delay: "0.9s" },
                { top: "68%", left: "78%", delay: "1.3s" },
                { top: "82%", left: "40%", delay: "1.7s" },
                { top: "35%", left: "50%", delay: "2.0s" },
              ].map((f, i) => (
                <div key={`cx_fleck_${i}`} className="cx-depths-fleck" style={{ top: f.top, left: f.left, animationDelay: f.delay }}>
                  <svg viewBox="0 0 10 10"><polygon points="5,0 10,5 5,10 0,5" /></svg>
                </div>
              ))}
              <div className="cx-depths-orbit">
                <svg viewBox="0 0 100 100">
                  <polygon points="50,6 55,12 50,18 45,12" />
                  <polygon points="94,50 88,55 82,50 88,45" />
                  <polygon points="50,94 45,88 50,82 55,88" />
                  <polygon points="6,50 12,45 18,50 12,55" />
                </svg>
              </div>
              <div className="cx-depths-sigil">
                <svg viewBox="0 0 100 100">
                  <polygon className="sigil-outer" points="50,6 94,50 50,94 6,50" />
                  <line className="sigil-accent-line" x1="50" y1="6"  x2="50" y2="94" />
                  <line className="sigil-accent-line" x1="6"  y1="50" x2="94" y2="50" />
                  <polygon className="sigil-mid"   points="50,20 80,50 50,80 20,50" />
                  <polygon className="sigil-core"  points="50,32 68,50 50,68 32,50" />
                  <polygon className="sigil-heart" points="50,42 58,50 50,58 42,50" />
                  <circle className="sigil-pinpoint" cx="50" cy="50" r="1.5" />
                  <polygon className="sigil-shimmer" points="50,6 94,50 50,94 6,50" />
                  <circle cx="50" cy="6"  r="1.2" fill="#ffffff" opacity="0.85" />
                  <circle cx="94" cy="50" r="1.2" fill="#ffffff" opacity="0.85" />
                  <circle cx="50" cy="94" r="1.2" fill="#ffffff" opacity="0.85" />
                  <circle cx="6"  cy="50" r="1.2" fill="#ffffff" opacity="0.85" />
                </svg>
              </div>
            </>
          )}
          {/* ORIGIN sigil — crowns the card, top-center. Just the mark. */}
          {(() => {
            const src2 = primaryOrigin(holoData, getCardOrigins(card.id));
            if (!src2) return null;
            return (
              <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
                <SourceSigil source={src2} size={30} />
              </div>
            );
          })()}
          {holoColor && (
            <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", zIndex: 2, whiteSpace: "nowrap", fontSize: 9, color: holoColor, background: "rgba(5,3,0,0.85)", border: `1px solid ${holoColor}66`, borderRadius: 2, padding: "2px 8px", fontWeight: 900, fontFamily: GM.font }}>
              {HOLO_TIERS[holoTier]?.label}
            </div>
          )}
        </div>
        <div style={{ fontSize: 15, fontWeight: 900, fontStyle: "italic",
          color: holoColor || GM.inkGoldBright, marginBottom: 4,
          fontFamily: GM.font }}>
          {card.name}
        </div>
        <div style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: 2, marginBottom: 10, fontFamily: GM.font }}>
          {(TYPE_LABELS[card.type] || card.type)?.toUpperCase()}
          {Number.isFinite(Number(card.level)) && card.type !== CARD_TYPES.AFFINITY
            ? ` · LVL ${card.level}` : ""}
        </div>
        {card.effect && (
          <div style={{ fontSize: 11, color: GM.vellumDim, lineHeight: 1.8,
            fontStyle: "italic", marginBottom: 12, fontFamily: GM.font }}>{card.effect}</div>
        )}
        {holoData?.holoCount > 0 && (
          <div style={{ fontSize: 10, color: holoColor, letterSpacing: 1 }}>
            {HOLO_TIERS[holoTier]?.label} · x{holoData.holoCount}
          </div>
        )}

        {/* ── H14 · THE LORE PAGE ──────────────────────────────
            One call. loreFor() returns caP'n's authored paragraph if
            this card has one and a paragraph composed from the card
            itself if it does not — so every card in the Codex has a
            page today and no page can ever contradict its card.
            The ✎ marks a draft: a reader can see at a glance which
            pages are canon and which are still scaffolding. */}
        <div style={{
          marginTop: 12, paddingTop: 10,
          borderTop: `1px solid ${GM.pageBorder}`,
        }}>
          <div style={{
            fontSize: 9, letterSpacing: 2.5, color: GM.vellumFaint,
            marginBottom: 6, fontFamily: GM.font,
          }}>
            ❧ LORE{!isAuthored(card.id) && <span title="A drafted page — composed from the card itself, not yet authored." style={{ marginLeft: 6, opacity: 0.7 }}>✎ draft</span>}
          </div>
          <div style={{
            fontSize: 11.5, color: GM.vellumDim, lineHeight: 1.85,
            fontFamily: GM.fontBody, textAlign: "left",
          }}>
            {loreFor(card)}
          </div>
        </div>
        {/* ── THE MARK (H18) — summon the Empire's record on this card.
            holoData IS the collection record (sources/sourceTiers/
            holoTiers ride on it), so The Mark reads it directly; unowned
            cards fall back to canonical origins. Read-only, always. */}
        <button onClick={() => setShowMark(m => !m)} style={{
          width: "100%", marginTop: 12, padding: "8px",
          background: showMark ? `${GM.inkGold}18` : "transparent",
          border: `1px solid ${GM.inkGold}66`,
          borderRadius: 2, cursor: "pointer", color: GM.inkGold,
          fontFamily: GM.font, fontSize: 10, fontWeight: 900, letterSpacing: 2.5,
        }}>▦ THE MARK</button>
        {showMark && (
          <TheMark
            being={{ key: card.id, name: card.name, type: card.type, game: "Terror" }}
            record={holoData}
            origins={getCardOrigins(card.id)}
            width="100%"
            style={{ marginTop: 10 }}
          />
        )}
        <button onClick={onClose} style={{
          width: "100%", marginTop: 14, padding: "9px",
          background: "transparent", border: `1px solid ${GM.inkBrown}66`,
          borderRadius: 2, cursor: "pointer", color: GM.vellumFaint,
          fontFamily: GM.font, fontSize: 11, letterSpacing: 1,
        }}>✕ CLOSE</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STAT CARD — for Records tab
// ─────────────────────────────────────────────────────────────
function StatCard({ label, value, color, sub }) {
  return (
    <div style={{
      flex: "1 1 140px",
      background: OBSIDIAN_SLAB,
      border: `1px solid ${GM.pageBorder}`,
      borderRadius: 10, padding: "14px 16px",
      display: "flex", flexDirection: "column", gap: 4,
      boxShadow: SHADOW_RAISED,
    }}>
      <div style={{ fontSize: 9, color: GM.vellumFaint,
        letterSpacing: 3, fontFamily: GM.font }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 900,
        color: color || GM.inkGoldBright, fontFamily: GM.fontUI,
        textShadow: `0 0 10px ${(color || GM.inkGoldBright)}44` }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: GM.vellumFaint, fontStyle: "italic", fontFamily: GM.font }}>{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// EMPTY STATE — reusable
// ─────────────────────────────────────────────────────────────
function EmptyState({ icon, title, desc, sub }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px",
      color: GM.vellumFaint, fontStyle: "italic", lineHeight: 2, fontFamily: GM.font }}>
      <div style={{ fontSize: 44, marginBottom: 16, opacity: 0.15 }}>{icon}</div>
      <div style={{ fontSize: 13, color: GM.vellumDim, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 11 }}>{desc}</div>
      {sub && <div style={{ fontSize: 10, opacity: 0.5, marginTop: 8 }}>{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN CODEX COMPONENT
// ─────────────────────────────────────────────────────────────
export default function Codex({ profile, onBack }) {
  const stage = useStageRect(); // C5 — the one canonical room rectangle
  const collection    = profile?.collection       || {};
  const storyVariants = profile?.storyVariants    || [];
  const easterEggs    = profile?.easterEggVariants || [];
  const grimPages     = profile?.grimoirePages    || [];
  const companions    = profile?.companions       || [];

  // ── Tab state ───────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(
    profile?.initialTab === "story"      ? "story"
    : profile?.initialTab === "records"  ? "records"
    : "collection"
  );

  // ── Collection filters ──────────────────────────────────────
  const [filterType,  setFilterType]  = useState("All");
  const [filterOwned, setFilterOwned] = useState("All");
  const [filterHolo,  setFilterHolo]  = useState("All");
  const [search,      setSearch]      = useState("");
  const [detailCard,  setDetailCard]  = useState(null);
  const [detailHolo,  setDetailHolo]  = useState(null);

  // Escape closes modal if open, otherwise closes Codex
  useModalKeys(detailCard ? () => { setDetailCard(null); setDetailHolo(null); } : onBack);

  // ── Stats ───────────────────────────────────────────────────
  const discoveredCount = Object.keys(collection).length;
  const holoCount   = Object.values(collection).filter(c => c?.holoCount > 0).length;
  const mythicCount = Object.values(collection).filter(c => c?.holoTier === "holo_mythic").length;
  const totalWins   = profile?.totalWins || 0;
  const completedDungeons = profile?.storyProgress?.completedDungeons || [];
  const defeatedOpponents = profile?.storyProgress?.defeatedOpponents || [];
  const chocolateCoins = profile?.chocolateCoins || 0;
  const divineCrests   = profile?.divineCrests   || 0;

  // ── Filtered cards ──────────────────────────────────────────
  const displayCards = useMemo(() => {
    return ALL_COLLECTIBLE.filter(card => {
      const owned   = !!collection[card.id];
      const holoData = collection[card.id];
      const hasHolo  = holoData?.holoCount > 0;
      if (filterType !== "All" && card.type !== filterType) return false;
      if (filterOwned === "Owned"   && !owned)   return false;
      if (filterOwned === "Unowned" && owned)    return false;
      if (filterOwned === "Holo"    && !hasHolo) return false;
      if (filterHolo !== "All" && holoData?.holoTier !== filterHolo) return false;
      if (search && !card.name.toLowerCase().includes(search.toLowerCase()) &&
          !card.effect?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [filterType, filterOwned, filterHolo, search, collection]);

  // ── Button style helper ─────────────────────────────────────
  const btnStyle = (active, color = GM.inkGold) => ({
    padding: "5px 12px",
    background: active
      ? `linear-gradient(135deg, ${color}26, transparent 60%), ${OBSIDIAN_SLAB_LIT}`
      : OBSIDIAN_SLAB,
    border: `1px solid ${active ? color : GM.pageBorder}`,
    borderRadius: 8, cursor: "pointer",
    color: active ? color : GM.vellumDim,
    fontFamily: GM.font, fontSize: 10, fontWeight: 900,
    whiteSpace: "nowrap", transition: "all 0.15s",
    boxShadow: active
      ? `inset 0 1px 0 ${color}44, 0 0 10px ${color}33`
      : "inset 0 1px 0 rgba(226,232,244,0.1), 0 2px 6px rgba(0,0,0,0.45)",
    textShadow: TEXT_SET,
  });

  return (
    <div style={{
      position: "fixed", inset: 0, background: "transparent",
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden", fontFamily: GM.font, color: GM.vellum,
    }}>
    {/* C5 backdrop wave (2026-08-18): the Archive lives inside the ONE
        canonical room rectangle; the root is transparent so the equipped
        backdrop letterboxes around it (the Emporium pattern). The sky/
        dust layers dock to the stage now, not the viewport. */}
    <div style={{
      position: "relative", width: stage.w, height: stage.h, flexShrink: 0,
      background: OBSIDIAN_BG,
      display: "flex", flexDirection: "column",
      overflow: "hidden",
    }}>
      <UnstableWorldBG />
      <StarDust count={22} style={{ position: "absolute" }} />
      <NoiseOverlay opacity={0.04} style={{ position: "absolute" }} />

      {/* Card detail modal */}
      {detailCard && (
        <CardDetailModal
          card={detailCard} holoData={detailHolo}
          onClose={() => { setDetailCard(null); setDetailHolo(null); }}
        />
      )}

      {/* ── HEADER ───────────────────────────────────────────── */}
      <div style={{
        padding: "14px 20px 0",
        maxWidth: 600, margin: "0 auto", width: "100%",
        position: "relative", zIndex: 1,
      }}>
        {/* Top row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <button onClick={onBack} style={{
            background: OBSIDIAN_SLAB, border: "none",
            borderTop: "1px solid rgba(226,232,244,0.2)",
            clipPath: facetTB(7),
            boxShadow: "inset 0 1px 0 rgba(226,232,244,0.14), inset 0 -3px 6px rgba(0,0,0,0.4)",
            filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.55))",
            color: GM.vellumDim, padding: "7px 15px",
            cursor: "pointer",
            fontFamily: GM.font, fontSize: 12, textShadow: TEXT_SET,
          }}>← Back</button>

          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 22, fontWeight: 900, fontStyle: "italic",
              color: "#f0cc60", textShadow: textGlow(GM.inkGoldBright),
              letterSpacing: 3, fontFamily: GM.font,
            }}>◆ Grimoire Codex</div>
          </div>

          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.fontUI, textShadow: textGlow(GM.inkGoldBright) }}>
              {discoveredCount}
              <span style={{ fontSize: 10, color: GM.vellumFaint }}> / {TOTAL}</span>
            </div>
            <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2, fontFamily: GM.font }}>DISCOVERED</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: OBSIDIAN_RECESS, borderRadius: 3,
          boxShadow: SHADOW_RECESS,
          marginBottom: 14, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${(discoveredCount / TOTAL) * 100}%`,
            background: GOLD_METAL_H,
            boxShadow: `0 0 10px ${GM.inkGoldBright}66`,
            transition: "width 0.5s ease",
          }} />
        </div>

        {/* Quick stats row */}
        <div style={{ display: "flex", gap: 16, marginBottom: 14 }}>
          {[
            { label: "HOLO",   value: holoCount,   color: GM.inkGold    },
            { label: "MYTHIC", value: mythicCount,  color: "#ff66cc"     },
            { label: "WINS",   value: totalWins,    color: GM.silver     },
          ].map(s => (
            <div key={s.label} style={{
              flex: 1, textAlign: "center", padding: "8px 4px",
              background: OBSIDIAN_SLAB,
              border: `1px solid ${GM.pageBorder}`, borderRadius: 8,
              boxShadow: SHADOW_RAISED,
            }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: s.color,
                fontFamily: GM.fontUI, textShadow: `0 0 8px ${s.color}44` }}>{s.value}</div>
              <div style={{ fontSize: 8, color: GM.vellumFaint, letterSpacing: 2, fontFamily: GM.font }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tab nav */}
        <div style={{ display: "flex", gap: 5, overflowX: "auto",
          paddingBottom: 4, marginBottom: 2 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: "8px 15px", cursor: "pointer",
              whiteSpace: "nowrap", transition: "all 0.18s",
              fontFamily: GM.font, fontSize: 10, letterSpacing: 1, fontWeight: 900,
              clipPath: facetTB(7), border: "none",
              borderTop: activeTab === t.id ? "1px solid #f5d878" : "1px solid transparent",
              background: activeTab === t.id ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
              color: activeTab === t.id ? "#ffe9a8" : GM.vellumFaint,
              boxShadow: activeTab === t.id
                ? `inset 0 1px 0 rgba(245,216,120,0.55), inset 0 0 18px ${GM.amethyst}30`
                : "inset 0 1px 0 rgba(226,232,244,0.12), inset 0 -4px 8px rgba(0,0,0,0.4)",
              filter: activeTab === t.id ? `drop-shadow(0 0 8px ${GM.inkGoldBright}40)` : "drop-shadow(0 2px 5px rgba(0,0,0,0.5))",
              textShadow: activeTab === t.id ? textGlow(GM.inkGoldBright) : TEXT_SET,
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* ── TAB CONTENT ──────────────────────────────────────── */}
      <div style={{
        flex: 1, overflowY: "auto",
        padding: "12px 20px 40px",
        maxWidth: 600, margin: "0 auto", width: "100%",
        boxSizing: "border-box", position: "relative", zIndex: 1,
      }}>

        {/* ══ COLLECTION TAB ══ */}
        {activeTab === "collection" && (<>
          {/* Search */}
          <input
            placeholder="🔍 Search cards..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: "100%", background: OBSIDIAN_RECESS,
              border: `1px solid ${GM.pageBorder}`, borderRadius: 10,
              boxShadow: SHADOW_RECESS,
              color: GM.vellum, padding: "10px 14px",
              fontFamily: GM.font, fontSize: 12,
              outline: "none", marginBottom: 10, boxSizing: "border-box",
              caretColor: GM.inkGold,
            }}
          />

          {/* Type filter */}
          <div style={{ display: "flex", gap: 5, overflowX: "auto",
            marginBottom: 8, paddingBottom: 4 }}>
            {TYPE_OPTIONS.map(t => (
              <button key={t} onClick={() => setFilterType(t)} style={btnStyle(filterType === t)}>
                {TYPE_ICONS[t] || ""} {TYPE_LABELS[t] || t}
              </button>
            ))}
          </div>

          {/* Owned / Holo filter */}
          <div style={{ display: "flex", gap: 5, marginBottom: 14, flexWrap: "wrap" }}>
            {["All","Owned","Unowned","Holo"].map(f => (
              <button key={f} onClick={() => setFilterOwned(f)}
                style={btnStyle(filterOwned === f, f === "Holo" ? GM.inkGold : GM.inkBrown)}>
                {f === "Holo" ? "✦ " : ""}{f}
              </button>
            ))}
            {Object.entries(HOLO_TIERS).map(([key, tier]) => (
              <button key={key}
                onClick={() => setFilterHolo(filterHolo === key ? "All" : key)}
                style={btnStyle(filterHolo === key, tier.color)}>
                {tier.label}
              </button>
            ))}
          </div>

          <div style={{ fontSize: 9, color: GM.vellumFaint, fontFamily: GM.font,
            letterSpacing: 3, marginBottom: 12, borderBottom: `1px solid ${GM.ruling}`, paddingBottom: 8 }}>
            {displayCards.length} CARDS INSCRIBED
          </div>

          {/* Card grid */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {displayCards.map(card => (
              <CardTile
                key={card.id} card={card}
                owned={!!collection[card.id]}
                holoData={collection[card.id] || null}
                onClick={(c, h) => { setDetailCard(c); setDetailHolo(h); }}
              />
            ))}
          </div>
          {displayCards.length === 0 && (
            <EmptyState icon="◇" title="No cards match your search"
              desc="Try adjusting your filters." />
          )}
        </>)}

        {/* ══ STORY VARIANTS TAB ══ */}
        {activeTab === "story" && (<>
          {storyVariants.length === 0 && easterEggs.length === 0 ? (
            <EmptyState icon="◈"
              title="No Story Variants yet."
              desc="Defeat dungeon bosses to earn variants that remember where they came from."
              sub="Easter Egg Variants are obtained through secrets. Nobody will tell you how." />
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
              {[...storyVariants.map(v => ({ ...v, isEgg: false })),
                ...easterEggs.map(v => ({ ...v, isEgg: true }))].map((v, i) => (
                <div key={i} style={{
                  width: 100, padding: 12, borderRadius: 10,
                  background: GM.pageMid,
                  border: `1px solid ${v.isEgg ? GM.inkGold : GM.inkBrown}`,
                  display: "flex", flexDirection: "column",
                  alignItems: "center", gap: 8,
                  boxShadow: "none",
                }}>
                  <div style={{
                    fontSize: 32, color: v.isEgg ? GM.inkGoldBright : GM.inkBrown,
                  }}>{v.isEgg ? "◈" : "◆"}</div>
                  <div style={{
                    fontSize: 9, color: v.isEgg ? GM.inkGoldBright : GM.vellumDim,
                    textAlign: "center", fontFamily: GM.font,
                    letterSpacing: 0.5, lineHeight: 1.5,
                  }}>{v.name || v.cardId}</div>
                  {v.dungeon && (
                    <div style={{ fontSize: 7, color: GM.vellumFaint, fontStyle: "italic", fontFamily: GM.font }}>
                      {v.dungeon}
                    </div>
                  )}
                  {v.isEgg && (
                    <div style={{
                      fontSize: 7, color: GM.inkGold, letterSpacing: 1,
                      background: `${GM.inkGold}18`, border: `1px solid ${GM.inkBrown}`,
                      padding: "2px 6px", borderRadius: 2,
                    }}>◈ EASTER EGG</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>)}

        {/* ══ GRIMOIRE PAGES TAB ══ */}
        {activeTab === "pages" && (<>
          <div style={{
            marginBottom: 16, padding: "10px 16px",
            background: OBSIDIAN_SLAB, border: `1px solid ${GM.pageBorder}`,
            borderLeft: `3px solid ${GM.inkGold}`,
            borderRadius: 8, fontSize: 11, color: GM.vellumDim,
            fontStyle: "italic", lineHeight: 1.7,
            boxShadow: SHADOW_RAISED, textShadow: TEXT_SET,
          }}>
            ◇ {grimPages.length} pages collected.
            The total is unknown. Or maybe it isn't.
          </div>
          {grimPages.length === 0 ? (
            <EmptyState icon="◇"
              title="No Grimoire Pages collected."
              desc="They hide in places you weren't supposed to go."
              sub="??? pages total. Or maybe more." />
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {grimPages.map((page, i) => (
                <div key={i} style={{
                  width: "calc(50% - 6px)", padding: "14px 16px",
                  borderRadius: 10,
                  background: GM.pageWarm,
                  border: `1px solid ${GM.inkBrown}`,
                  display: "flex", flexDirection: "column", gap: 8,
                  boxSizing: "border-box",
                }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <div style={{
                      fontSize: 10, color: GM.inkGold,
                      fontFamily: GM.font, letterSpacing: 1,
                    }}>Page {page.pageId}</div>
                    <div style={{
                      fontSize: 8, color: GM.inkBrown,
                      fontFamily: GM.font,
                    }}>◇</div>
                  </div>
                  <div style={{
                    fontSize: 11, color: GM.vellumDim,
                    fontStyle: "italic", lineHeight: 1.9, fontFamily: GM.font,
                  }}>
                    {page.fragment || "The text fades when you try to read it."}
                  </div>
                  {page.dungeon && (
                    <div style={{ fontSize: 8, color: GM.vellumFaint, fontFamily: GM.font }}>
                      Found in: {page.dungeon}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>)}

        {/* ══ COMPANIONS TAB ══ */}
        {activeTab === "companions" && (<>
          {companions.length === 0 ? (
            <EmptyState icon="✦"
              title="No companions yet."
              desc="Clear dungeons to unlock companions that follow you through the Plaza."
              sub="Each companion is earned, not bought." />
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
              {companions.map((c, i) => (
                <div key={i} style={{
                  width: 100, padding: 14, borderRadius: 10,
                  background: GM.pageMid,
                  border: `1px solid ${c.active ? GM.inkGold : GM.inkBrown}`,
                  display: "flex", flexDirection: "column",
                  alignItems: "center", gap: 8,
                  boxShadow: c.active ? `0 0 14px ${GM.inkGoldBright}44` : "none",
                }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: "50%",
                    background: GM.pageLight,
                    border: `2px solid ${c.active ? GM.inkGold : GM.inkBrown}`,
                    display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 22,
                    boxShadow: c.active ? `0 0 12px ${GM.inkGoldBright}66` : "none",
                  }}>✦</div>
                  <div style={{
                    fontSize: 9, color: GM.inkGoldBright, textAlign: "center",
                    fontFamily: GM.font, lineHeight: 1.4,
                  }}>{c.name || c.id}</div>
                  {c.unlockedBy && (
                    <div style={{ fontSize: 7, color: GM.vellumFaint,
                      fontStyle: "italic", textAlign: "center", fontFamily: GM.font }}>
                      {c.unlockedBy}
                    </div>
                  )}
                  {c.active && (
                    <div style={{
                      fontSize: 7, color: GM.inkGoldBright,
                      background: `${GM.inkGold}22`,
                      border: `1px solid ${GM.inkBrown}`,
                      padding: "2px 8px", borderRadius: 2,
                      letterSpacing: 1,
                    }}>ACTIVE</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>)}

        {/* ══ RECORDS TAB ══ */}
        {activeTab === "records" && (<>
          {/* Stat cards */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
            <StatCard label="TOTAL WINS"        value={totalWins}
              color={GM.inkGold} />
            {/* ◕ Both currencies, side by side, always. The player should
                never have to guess which number the shop takes. */}
            <StatCard label="CHOCOLATE COINS"   value={chocolateCoins.toLocaleString()}
              color={GM.inkGold} sub="what the Emporium takes" />
            <StatCard label="DIVINE CRESTS"     value={divineCrests.toLocaleString()}
              color={GM.inkGold} sub="the Empire's reserve" />
            <StatCard label="CARDS DISCOVERED"  value={`${discoveredCount}/${TOTAL}`}
              color="#22d3ee" />
            <StatCard label="DUNGEONS CLEARED"  value={completedDungeons.length}
              color="#dc2626" sub="of 7 dungeons" />
            <StatCard label="OPPONENTS FELLED"  value={defeatedOpponents.length}
              color="#9333ea" />
            <StatCard label="HOLOS EARNED"      value={holoCount}
              color={GM.inkGold} sub={`${mythicCount} mythic`} />
          </div>

          {/* Dungeon progress */}
          {completedDungeons.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 3, fontFamily: GM.font,
                marginBottom: 10 }}>DUNGEONS CLEARED</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {completedDungeons.map((d, i) => (
                  <div key={i} style={{
                    padding: "10px 14px", borderRadius: 8,
                    background: OBSIDIAN_SLAB,
                    border: `1px solid ${GM.pageBorder}`,
                    boxShadow: SHADOW_RAISED,
                    display: "flex", justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <div style={{ fontSize: 11, color: GM.vellum,
                      fontStyle: "italic", fontFamily: GM.font }}>{d}</div>
                    <div style={{ fontSize: 9, color: GM.inkGoldDim }}>◈ CLEARED</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Opponents felled */}
          {defeatedOpponents.length > 0 && (
            <div>
              <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 3, fontFamily: GM.font,
                marginBottom: 10 }}>OPPONENTS DEFEATED</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {defeatedOpponents.map((opp, i) => (
                  <div key={i} style={{
                    padding: "6px 12px", borderRadius: 8,
                    background: OBSIDIAN_SLAB,
                    border: `1px solid ${GM.pageBorder}`,
                    boxShadow: SHADOW_RAISED,
                    fontSize: 10, color: GM.vellumDim,
                    fontFamily: GM.font, letterSpacing: 1, textShadow: TEXT_SET,
                  }}>◆ {opp}</div>
                ))}
              </div>
            </div>
          )}

          {totalWins === 0 && completedDungeons.length === 0 && (
            <EmptyState icon="⊡"
              title="Your record is empty."
              desc="Win Solrends. Clear dungeons. The Codex remembers everything."
              sub="Every victory is permanent." />
          )}
        </>)}

      </div>
    </div>
    </div>
  );
}